import React, { useEffect, useState, useCallback } from "react";
import { Loader2, LogOut, Plus, Pencil, Trash2, ShieldAlert, Inbox, FileText, ExternalLink, Star } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SettingsEditor from "@/components/admin/SettingsEditor";

const fieldCls =
  "w-full min-h-[46px] rounded-md border border-[#D9E0E4] bg-white px-3.5 text-[15px] text-navy " +
  "focus:outline-none focus:ring-2 focus:ring-teal focus:border-teal transition-colors";

const emptyPost = { title: "", excerpt: "", body: "", cover_image: "", category: "Insights", published: true };

const STATUS_META = {
  new: { label: "New", cls: "bg-[#EAF1F4] text-navy border-[#B9CEDA]" },
  in_review: { label: "In review", cls: "bg-[#FBF1E4] text-[#8A5A14] border-[#E8CE9E]" },
  quoted: { label: "Quoted", cls: "bg-[#E7F3EC] text-success border-[#B3DCC3]" },
  closed: { label: "Closed", cls: "bg-[#F1F2F1] text-[#5D6974] border-[#D9E0E4]" },
};
const STATUS_ORDER = ["new", "in_review", "quoted", "closed"];

function LoginGate() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (!res.ok) setError(res.error || "Login failed");
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-background px-5">
      <form onSubmit={onSubmit} className="w-full max-w-md rounded-lg border border-[#D9E0E4] bg-white p-8" data-testid="admin-login-form">
        <div className="flex justify-center"><Logo /></div>
        <h1 className="mt-8 font-heading font-bold text-[24px] text-navy text-center">CMS admin sign in</h1>
        <p className="mt-2 text-[14px] text-muted-foreground text-center">
          Enter the admin email and password to manage the site.
        </p>
        <div className="mt-6 space-y-4">
          <div>
            <label className="block text-[13px] font-semibold text-navy mb-1.5">Email</label>
            <input
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={fieldCls}
              placeholder="admin@example.com"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-navy mb-1.5">Password</label>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={fieldCls}
              placeholder="••••••••"
              data-testid="admin-login-password"
            />
          </div>
          {error && (
            <p className="text-[13px] text-[#B42318] bg-[#FEF3F2] border border-[#F4B7B0] rounded-md p-2.5" data-testid="admin-login-error">
              {error}
            </p>
          )}
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="admin-login-submit"
          className="mt-6 w-full min-h-[48px] rounded-md bg-navy text-white font-semibold hover:bg-[#123B5A] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
        </button>
        <p className="mt-4 text-[12px] text-muted-foreground text-center">
          Access is restricted to authorized administrators.
        </p>
      </form>
    </div>
  );
}

function PostEditor({ open, onClose, post, onSaved }) {
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);
  const isEdit = !!post?.id;

  useEffect(() => {
    setForm(post ? { ...emptyPost, ...post } : emptyPost);
  }, [post, open]);

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/posts/${post.id}`, form);
        toast.success("Post updated.");
      } else {
        await api.post("/posts", form);
        toast.success("Post published.");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save post.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit insight" : "New insight"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-[13px] font-semibold text-navy mb-1.5">Title</label>
            <input className={fieldCls} value={form.title} data-testid="admin-post-title"
              onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-semibold text-navy mb-1.5">Category</label>
              <input className={fieldCls} value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-navy mb-1.5">Cover image URL</label>
              <input className={fieldCls} value={form.cover_image} placeholder="https://…"
                onChange={(e) => setForm({ ...form, cover_image: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-navy mb-1.5">Excerpt</label>
            <textarea className={`${fieldCls} py-3 min-h-[70px]`} value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-navy mb-1.5">Body</label>
            <textarea className={`${fieldCls} py-3 min-h-[220px]`} value={form.body} data-testid="admin-post-body"
              placeholder="Write your article. Separate paragraphs with new lines."
              onChange={(e) => setForm({ ...form, body: e.target.value })} />
          </div>
          <label className="flex items-center gap-2.5 text-[14px] text-navy">
            <input type="checkbox" className="h-4 w-4 accent-[#0B6E69]" checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })} />
            Published
          </label>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="min-h-[44px] px-5 rounded-md border border-[#D9E0E4] text-navy font-semibold hover:bg-[#EAF1F4]">
            Cancel
          </button>
          <button onClick={save} disabled={saving} data-testid="admin-post-save"
            className="min-h-[44px] px-5 rounded-md bg-teal text-white font-semibold hover:bg-[#075652] inline-flex items-center gap-2 disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {isEdit ? "Save changes" : "Publish"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const emptyReview = { name: "", company: "", role: "", country: "", quote: "", rating: 5, published: true };

function ReviewEditor({ open, onClose, review, onSaved }) {
  const [form, setForm] = useState(emptyReview);
  const [saving, setSaving] = useState(false);
  const isEdit = !!review?.id;

  useEffect(() => { setForm(review ? { ...emptyReview, ...review } : emptyReview); }, [review, open]);

  const save = async () => {
    if (!form.name.trim() || !form.quote.trim()) { toast.error("Name and review text are required."); return; }
    setSaving(true);
    try {
      if (isEdit) { await api.put(`/reviews/${review.id}`, form); toast.success("Review updated."); }
      else { await api.post("/reviews", form); toast.success("Review published."); }
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save review."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit review" : "New review"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-semibold text-navy mb-1.5">Customer name</label>
              <input className={fieldCls} value={form.name} data-testid="admin-review-name" onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-navy mb-1.5">Country</label>
              <input className={fieldCls} value={form.country} placeholder="e.g. UAE, Saudi Arabia, USA" onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-navy mb-1.5">Company</label>
              <input className={fieldCls} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-navy mb-1.5">Role / title</label>
              <input className={fieldCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-navy mb-1.5">Review text</label>
            <textarea className={`${fieldCls} py-3 min-h-[120px]`} value={form.quote} data-testid="admin-review-quote" onChange={(e) => setForm({ ...form, quote: e.target.value })} />
          </div>
          <div className="flex items-center gap-6">
            <div>
              <label className="block text-[13px] font-semibold text-navy mb-1.5">Rating</label>
              <select className={fieldCls} value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}>
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2.5 text-[14px] text-navy mt-6">
              <input type="checkbox" className="h-4 w-4 accent-[#0B6E69]" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
              Published
            </label>
          </div>
          <p className="text-[12px] text-muted-foreground">Only add reviews you have permission to publish. We do not display fabricated testimonials.</p>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="min-h-[44px] px-5 rounded-md border border-[#D9E0E4] text-navy font-semibold hover:bg-[#EAF1F4]">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="admin-review-save"
            className="min-h-[44px] px-5 rounded-md bg-teal text-white font-semibold hover:bg-[#075652] inline-flex items-center gap-2 disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {isEdit ? "Save changes" : "Publish"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Dashboard({ user, logout }) {
  const [posts, setPosts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewEditorOpen, setReviewEditorOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [tab, setTab] = useState("posts");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, q, rv] = await Promise.all([
        api.get("/posts?all=true"),
        api.get("/quotes"),
        api.get("/reviews?all=true"),
      ]);
      setPosts(p.data);
      setQuotes(q.data);
      setReviews(rv.data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/posts/${id}`);
      toast.success("Post deleted.");
      load();
    } catch (e) { toast.error("Failed to delete."); }
  };

  const delReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/reviews/${id}`);
      toast.success("Review deleted.");
      load();
    } catch (e) { toast.error("Failed to delete."); }
  };

  const updateStatus = async (id, status) => {
    setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
    try {
      await api.patch(`/quotes/${id}/status`, { status });
      toast.success(`Marked as ${STATUS_META[status]?.label || status}.`);
    } catch (e) {
      toast.error("Failed to update status.");
      load();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-[#D9E0E4] bg-white">
        <div className="container-page flex items-center justify-between h-[68px]">
          <Logo size={34} />
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-[13px] text-muted-foreground">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-navy hover:text-teal">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-page py-10">
        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <TabsList>
              <TabsTrigger value="posts" data-testid="admin-tab-posts">Insights ({posts.length})</TabsTrigger>
              <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Enquiries ({quotes.length})</TabsTrigger>
              <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Reviews ({reviews.length})</TabsTrigger>
              <TabsTrigger value="settings" data-testid="admin-tab-settings">Site settings</TabsTrigger>
            </TabsList>
            {tab === "posts" && (
              <button onClick={() => { setEditing(null); setEditorOpen(true); }} data-testid="admin-new-post"
                className="inline-flex items-center gap-2 min-h-[44px] px-5 rounded-md bg-teal text-white font-semibold hover:bg-[#075652]">
                <Plus className="h-4 w-4" /> New insight
              </button>
            )}
            {tab === "reviews" && (
              <button onClick={() => { setEditingReview(null); setReviewEditorOpen(true); }} data-testid="admin-new-review"
                className="inline-flex items-center gap-2 min-h-[44px] px-5 rounded-md bg-teal text-white font-semibold hover:bg-[#075652]">
                <Plus className="h-4 w-4" /> New review
              </button>
            )}
          </div>

          <TabsContent value="posts" className="mt-6">
            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>
            ) : posts.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[#C3CDD3] bg-white p-12 text-center">
                <FileText className="h-8 w-8 text-teal/50 mx-auto" />
                <p className="mt-4 text-[15px] text-navy font-medium">No insights yet</p>
                <p className="text-[13px] text-muted-foreground">Create your first article to populate the Insights page.</p>
              </div>
            ) : (
              <div className="rounded-lg border border-[#D9E0E4] bg-white divide-y divide-[#D9E0E4]">
                {posts.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-4 p-4" data-testid={`admin-post-row-${p.slug}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-heading font-semibold text-[15px] text-navy truncate">{p.title}</p>
                        {!p.published && <span className="text-[10px] uppercase font-bold text-copper bg-[#FBF1E4] px-1.5 py-0.5 rounded">Draft</span>}
                      </div>
                      <p className="text-[12.5px] text-muted-foreground">{p.category} · /{p.slug}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <a href={`/insights/${p.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-muted-foreground hover:text-teal" aria-label="View"><ExternalLink className="h-4 w-4" /></a>
                      <button onClick={() => { setEditing(p); setEditorOpen(true); }} className="p-2 text-muted-foreground hover:text-teal" aria-label="Edit" data-testid={`admin-edit-${p.slug}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => del(p.id)} className="p-2 text-muted-foreground hover:text-[#B42318]" aria-label="Delete" data-testid={`admin-delete-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="quotes" className="mt-6">
            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>
            ) : quotes.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[#C3CDD3] bg-white p-12 text-center">
                <Inbox className="h-8 w-8 text-teal/50 mx-auto" />
                <p className="mt-4 text-[15px] text-navy font-medium">No enquiries yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {quotes.map((q) => (
                  <div key={q.id} className="rounded-lg border border-[#D9E0E4] bg-white p-5" data-testid={`admin-quote-${q.reference}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <span className="font-heading font-bold text-teal text-[14px]">{q.reference}</span>
                        <span className="font-semibold text-navy text-[15px]">{q.full_name}</span>
                        {q.company_name && <span className="text-muted-foreground text-[13px]">· {q.company_name}</span>}
                        {q.enquiry_type === "freight_rate" && (
                          <span className="text-[10px] uppercase font-bold text-teal bg-[#E3F0EF] px-1.5 py-0.5 rounded">Freight rate</span>
                        )}
                      </div>
                      <span className="text-[12px] text-muted-foreground">{new Date(q.created_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${STATUS_META[q.status || "new"]?.cls}`} data-testid={`quote-status-badge-${q.reference}`}>
                        {STATUS_META[q.status || "new"]?.label}
                      </span>
                      <span className="text-[12px] text-muted-foreground">Set status:</span>
                      {STATUS_ORDER.map((s) => (
                        <button
                          key={s}
                          onClick={() => updateStatus(q.id, s)}
                          data-testid={`quote-status-${s}-${q.reference}`}
                          className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md border transition-colors ${
                            (q.status || "new") === s
                              ? "bg-navy text-white border-navy"
                              : "bg-white text-navy border-[#D9E0E4] hover:bg-[#EAF1F4]"
                          }`}
                        >
                          {STATUS_META[s].label}
                        </button>
                      ))}
                    </div>
                    <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1.5 text-[13px]">
                      <p><span className="text-muted-foreground">Email:</span> <a className="text-teal" href={`mailto:${q.work_email}`}>{q.work_email}</a></p>
                      <p><span className="text-muted-foreground">Phone:</span> {q.phone}</p>
                      {q.enquiry_type !== "freight_rate" && (<>
                        <p><span className="text-muted-foreground">Service:</span> {q.service || "—"}</p>
                        <p><span className="text-muted-foreground">Product:</span> {q.product_name || "—"}</p>
                        <p><span className="text-muted-foreground">HS Code:</span> {q.hs_code || "—"}</p>
                        <p><span className="text-muted-foreground">Quantity:</span> {q.quantity || "—"} {q.unit || ""}</p>
                        <p><span className="text-muted-foreground">Sourcing:</span> {q.sourcing_country || "—"}</p>
                        <p><span className="text-muted-foreground">Destination:</span> {q.delivery_destination || "—"}</p>
                        <p><span className="text-muted-foreground">Timeline:</span> {q.timeline || "—"}</p>
                      </>)}
                    </div>
                    {q.message && <p className="mt-3 text-[13.5px] text-navy bg-[#F6F7F5] rounded-md p-3">{q.message}</p>}
                    {q.enquiry_type === "freight_rate" && (
                      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1.5 text-[13px] border-t border-[#EAF1F4] pt-3">
                        <p><span className="text-muted-foreground">Commodity:</span> {q.commodity || "—"}</p>
                        <p><span className="text-muted-foreground">HS Code:</span> {q.hs_code || "—"}</p>
                        <p><span className="text-muted-foreground">Origin:</span> {q.origin_city || "—"} {q.origin_term ? `(${q.origin_term})` : ""}</p>
                        <p><span className="text-muted-foreground">Dest:</span> {q.destination_city || "—"} {q.destination_term ? `(${q.destination_term})` : ""}</p>
                        <p><span className="text-muted-foreground">Mode:</span> {q.mode || "—"}</p>
                        <p><span className="text-muted-foreground">Container:</span> {q.container_type || "—"}</p>
                        <p><span className="text-muted-foreground">Cargo:</span> {q.cargo_nature || "—"}</p>
                        <p><span className="text-muted-foreground">Volume:</span> {q.cargo_volume || "—"} {q.unit || ""}</p>
                        <p><span className="text-muted-foreground">Ready:</span> {q.cargo_ready_date || "—"}</p>
                      </div>
                    )}
                    {q.attachments?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {q.attachments.map((a, i) => (
                          <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-[12.5px] text-teal border border-[#D9E0E4] rounded px-2.5 py-1">
                            <FileText className="h-3.5 w-3.5" /> {a.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>
            ) : reviews.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[#C3CDD3] bg-white p-12 text-center">
                <Star className="h-8 w-8 text-teal/50 mx-auto" />
                <p className="mt-4 text-[15px] text-navy font-medium">No reviews yet</p>
                <p className="text-[13px] text-muted-foreground">Add genuine, permission-cleared customer reviews. They appear on the homepage once published.</p>
              </div>
            ) : (
              <div className="rounded-lg border border-[#D9E0E4] bg-white divide-y divide-[#D9E0E4]">
                {reviews.map((r) => (
                  <div key={r.id} className="flex items-start justify-between gap-4 p-4" data-testid={`admin-review-row-${r.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-heading font-semibold text-[15px] text-navy">{r.name}</p>
                        {r.company && <span className="text-[12.5px] text-muted-foreground">· {r.company}</span>}
                        {r.country && <span className="text-[11px] font-semibold text-teal bg-[#E3F0EF] px-1.5 py-0.5 rounded">{r.country}</span>}
                        {!r.published && <span className="text-[10px] uppercase font-bold text-copper bg-[#FBF1E4] px-1.5 py-0.5 rounded">Draft</span>}
                        <span className="text-[11px] text-copper">{"★".repeat(r.rating || 0)}</span>
                      </div>
                      <p className="mt-1 text-[13.5px] text-muted-foreground line-clamp-2">“{r.quote}”</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => { setEditingReview(r); setReviewEditorOpen(true); }} className="p-2 text-muted-foreground hover:text-teal" aria-label="Edit" data-testid={`admin-edit-review-${r.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => delReview(r.id)} className="p-2 text-muted-foreground hover:text-[#B42318]" aria-label="Delete" data-testid={`admin-delete-review-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <SettingsEditor />
          </TabsContent>
        </Tabs>
      </div>

      <PostEditor open={editorOpen} onClose={() => setEditorOpen(false)} post={editing} onSaved={load} />
      <ReviewEditor open={reviewEditorOpen} onClose={() => setReviewEditorOpen(false)} review={editingReview} onSaved={load} />
    </div>
  );
}

export default function Admin() {
  const { user, loading, logout, checkAuth } = useAuth();
  const [rechecking, setRechecking] = useState(!user);

  useEffect(() => {
    // Ensure auth is verified when landing on /admin (AuthProvider skips the
    // check on public routes to avoid 401 noise, so verify here).
    let active = true;
    (async () => {
      if (!user) await checkAuth();
      if (active) setRechecking(false);
    })();
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || rechecking) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal" />
      </div>
    );
  }

  if (!user) {
    return (<><SEO title="Admin | Premier Global Sourcing" description="CMS admin" path="/admin" /><LoginGate /></>);
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-5">
        <div className="max-w-md text-center rounded-lg border border-[#D9E0E4] bg-white p-8">
          <ShieldAlert className="h-10 w-10 text-copper mx-auto" />
          <h1 className="mt-5 font-heading font-bold text-[22px] text-navy">Access restricted</h1>
          <p className="mt-2 text-[14px] text-muted-foreground">
            The account <strong>{user.email}</strong> is not on the admin allowlist.
          </p>
          <button onClick={logout} className="mt-6 min-h-[46px] px-6 rounded-md border border-[#D9E0E4] text-navy font-semibold hover:bg-[#EAF1F4]">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (<><SEO title="Admin Dashboard | Premier Global Sourcing" description="CMS admin" path="/admin" /><Dashboard user={user} logout={logout} /></>);
}
